import React from 'react';
import { X, Share, MoreVertical, Menu, Download, Check, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { usePWAInstall } from '@/hooks/usePWAInstall';

interface AddToHomeScreenProps {
  embedded?: boolean;
  onInstalled?: () => void;
}

const AddToHomeScreen: React.FC<AddToHomeScreenProps> = ({ embedded = false, onInstalled }) => {
  const [isVisible, setIsVisible] = React.useState(false);
  const { t } = useTranslation();
  const { 
    isInstallable, 
    isInstalled, 
    isInstalling, 
    platform, 
    installApp,
    installOutcome 
  } = usePWAInstall();

  React.useEffect(() => {
    // If embedded, always show the component
    if (embedded) {
      setIsVisible(true);
      return;
    }

    // Check if user has completed onboarding or already prompted
    const hasPrompted = localStorage.getItem('homeScreenPromptShown');
    const userData = localStorage.getItem('userData');
    const isOnboardingComplete = userData ? JSON.parse(userData).onboardingCompleted : false;
    
    // Don't show prompt if already prompted or just completed onboarding
    if (hasPrompted || isOnboardingComplete) return;

    // Add a short delay to ensure the component renders after page load
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 1500);

    return () => clearTimeout(timer);
  }, [embedded]);

  React.useEffect(() => {
    if (isInstalled && onInstalled) {
      onInstalled();
    }
  }, [isInstalled, onInstalled]);

  const handleDismiss = () => {
    setIsVisible(false);
    localStorage.setItem('homeScreenPromptShown', 'true');
  };

  const handleInstallClick = async () => {
    const success = await installApp();
    if (success && onInstalled) {
      onInstalled();
    }
  };

  // Don't render anything if not visible and not embedded
  if (!isVisible && !embedded) return null;

  // Show success state if already installed
  if (isInstalled) {
    return (
      <div className={embedded ? "text-center py-4" : "fixed bottom-20 left-4 right-4 bg-white rounded-lg shadow-lg p-4 z-50 animate-fade-in border border-green-200"}>
        <div className="flex flex-col items-center gap-3">
          <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
            <Check className="w-6 h-6 text-green-600" />
          </div>
          <div className="text-center">
            <h3 className="font-semibold text-green-800">{t('install.alreadyInstalled')}</h3>
            <p className="text-sm text-green-600 mt-1">{t('install.successMessage')}</p>
          </div>
        </div>
        {!embedded && (
          <button 
            onClick={handleDismiss}
            className="absolute top-2 right-2 text-gray-500 hover:text-gray-700 rounded-full p-1"
            aria-label="Fermer"
          >
            <X size={20} />
          </button>
        )}
      </div>
    );
  }

  const instructions = (
    <div className={embedded ? "" : "fixed bottom-20 left-4 right-4 bg-white rounded-lg shadow-lg p-4 z-50 animate-fade-in border border-teal-200"}>
      <div className="flex items-start">
        <div className="flex-1">
          {!embedded && <h3 className="font-semibold text-gray-800 mb-2">{t('install.title')}</h3>}
          
          {/* Show install button if native prompt is available */}
          {isInstallable && (
            <div className="mb-4">
              <Button 
                onClick={handleInstallClick}
                disabled={isInstalling}
                className="w-full h-12 bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700"
              >
                {isInstalling ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    {t('install.installing')}
                  </>
                ) : (
                  <>
                    <Download className="w-5 h-5 mr-2" />
                    {t('install.button')}
                  </>
                )}
              </Button>
              {installOutcome === 'dismissed' && (
                <p className="text-sm text-amber-600 mt-2 text-center">
                  {t('install.promptNotAvailable')}
                </p>
              )}
            </div>
          )}
          
          {/* Show manual instructions for iOS or when native prompt is not available */}
          {(platform === 'ios' || !isInstallable) && (
            <>
              {platform === 'ios' && (
                <p className="text-sm text-amber-600 mb-3">{t('install.iosNote')}</p>
              )}
              
              {platform === 'ios' ? (
                <div className="text-sm text-gray-600">
                  <div className="mb-2 flex items-center">
                    <span>{t('install.instructions.ios.step1')}</span>
                    <Share size={18} className="mx-1 text-blue-500" />
                  </div>
                  <div className="flex items-center">
                    <span>{t('install.instructions.ios.step2')}</span>
                    <span className="mx-1 bg-gray-100 rounded px-1 py-0.5 text-xs font-medium">{t('install.instructions.ios.addToHome')}</span>
                  </div>
                  <div className="mt-2 border border-gray-200 rounded p-2 bg-gray-50">
                    <div className="flex items-center justify-center mb-1">
                      <Share size={20} className="text-blue-500" />
                    </div>
                    <div className="flex flex-col items-center">
                      <div className="w-20 h-6 bg-white rounded-md border border-gray-300 mb-1 flex items-center justify-center">
                        <span className="text-xs">{t('install.instructions.ios.addToHome')}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-sm text-gray-600">
                  <div className="mb-2 flex items-center">
                    <span>{t('install.instructions.android.step1')}</span>
                    <MoreVertical size={18} className="mx-1 text-gray-700" />
                    <span>{t('install.or')}</span>
                    <Menu size={18} className="mx-1 text-gray-700" />
                  </div>
                  <div className="flex items-center">
                    <span>{t('install.instructions.android.step2')}</span>
                    <span className="mx-1 bg-gray-100 rounded px-1 py-0.5 text-xs font-medium">{t('install.instructions.android.addToHome')}</span>
                  </div>
                  <div className="mt-2 border border-gray-200 rounded p-2 bg-gray-50">
                    <div className="flex items-center justify-end mb-1">
                      <MoreVertical size={20} className="text-gray-700" />
                    </div>
                    <div className="flex flex-col items-center">
                      <div className="w-48 h-6 bg-white rounded-md border border-gray-300 mb-1 flex items-center justify-center">
                        <span className="text-xs">{t('install.instructions.android.addToHome')}</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
        {!embedded && (
          <button 
            onClick={handleDismiss}
            className="text-gray-500 hover:text-gray-700 rounded-full p-1"
            aria-label="Fermer"
          >
            <X size={20} />
          </button>
        )}
      </div>
    </div>
  );

  return instructions;
};

export default AddToHomeScreen;
